import { Card } from "@components/Card";
import { UUID } from "io-ts-types/UUID";
import React from "react";

import {
  UserResponse,
  PersonalUserResponse,
} from "@every.org/common/src/codecs/entities";
import { getUserFullName } from "@every.org/common/src/helpers/username";

import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "src/components/Button";
import { RadioInput } from "src/components/RadioInput";
import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { nonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import {
  Fieldset,
  RadioGroup,
} from "src/pages/Fundraiser/EditForm/FundraiserOwnerPicker/styles";
import { FundraiserEditFormKind } from "src/pages/Fundraiser/EditForm/types";
import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  verticalStackCss,
  spacing,
} from "src/theme/spacing";

interface FundraiserOwnerPickerProps {
  creatorNonprofitId: UUID | null;
  loggedInUser?: PersonalUserResponse;
  fundraiserCreator?: UserResponse;
  adminForIds?: UUID[];
  onChange: (id: UUID | null) => void;
  setShowDeleteModal: (open: boolean) => void;
  setShowEndModal: (open: boolean) => void;
  isActive?: boolean;
  kind: FundraiserEditFormKind;
  canPickOwner: boolean;
  canEdit?: boolean;
  advancedSection?: React.ReactNode;
}

export function FundraiserOwnerPicker({
  creatorNonprofitId,
  loggedInUser,
  fundraiserCreator,
  adminForIds,
  onChange,
  setShowDeleteModal,
  setShowEndModal,
  isActive,
  kind,
  canPickOwner,
  canEdit,
  advancedSection,
}: FundraiserOwnerPickerProps) {
  return (
    <Card css={verticalStackCss.xxl}>
      {canPickOwner && adminForIds && adminForIds.length > 0 && (
        <Fieldset name={"owner"} css={{ marginBottom: spacing.xxxl }}>
          <legend>Fundraiser owner</legend>
          <p>
            By default, fundraisers created by admins of nonprofits are shown as
            being run by the nonprofit. If you’d like to make a personal
            fundraiser instead, select your name below.
          </p>
          <RadioGroup>
            {adminForIds.map((id) => (
              <NonprofitRadioInput
                key={id}
                nonprofitId={id}
                creatorNonprofitId={creatorNonprofitId}
                onChange={onChange}
              />
            ))}
            <RadioInput
              data-tname={"FundraiserOwner_User"}
              name={"owner"}
              value={"null"}
              checked={!creatorNonprofitId}
              onChange={() => onChange(null)}
              label={
                kind === FundraiserEditFormKind.EDIT
                  ? fundraiserCreator && getUserFullName(fundraiserCreator)
                  : loggedInUser && getUserFullName(loggedInUser)
              }
            />
          </RadioGroup>
        </Fieldset>
      )}
      {canEdit && (
        <div css={verticalStackCss.l}>
          <div
            css={[
              horizontalStackCss.m,
              { alignItems: "center" },
              cssForMediaSize({
                max: MediaSize.MEDIUM,
                css: verticalStackCss.m,
              }),
            ]}
          >
            <Button
              data-tname="FundraiseEdit_Cancel"
              role={ButtonRole.SECONDARY}
              size={ButtonSize.SMALL}
              css={{ minWidth: "180px", maxHeight: "58px" }}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => {
                  setShowEndModal(true);
                },
              }}
            >
              {isActive ? "End fundraiser" : "Resume fundraiser"}
            </Button>
            <p>
              {isActive
                ? "Donors will no longer be able to contribute, but the fundraiser will still be visible. You can resume your fundraiser at any time to start collecting donations again."
                : "Donors will be able to contribute again. You can stop your fundraiser at any time later again."}
            </p>
          </div>
          <div
            css={[
              horizontalStackCss.m,
              { alignItems: "center" },
              cssForMediaSize({
                max: MediaSize.MEDIUM,
                css: verticalStackCss.m,
              }),
            ]}
          >
            <Button
              data-tname="FundraiseEdit_Cancel"
              role={ButtonRole.SECONDARY}
              css={{
                minWidth: "180px",
                borderColor: "#D95C35",
                maxHeight: "58px",
              }}
              contentCss={{ color: "#D95C35" }}
              size={ButtonSize.SMALL}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => {
                  setShowDeleteModal(true);
                },
              }}
            >
              Delete fundraiser
            </Button>
            <p>
              This will permenantly remove the fundraiser from Every.org. This
              will NOT delete any donations already made through this
              fundraiser.
            </p>
          </div>
        </div>
      )}
      {advancedSection}
    </Card>
  );
}

function NonprofitRadioInput({
  nonprofitId,
  creatorNonprofitId,
  onChange,
}: {
  nonprofitId: UUID;
  creatorNonprofitId: UUID | null;
  onChange: (id: UUID) => void;
}) {
  const name = nonprofitOrUndefined(useNonprofit({ id: nonprofitId }))?.name;

  if (!name) {
    return null;
  }

  return (
    <RadioInput
      data-tname={"FundraiserOwner_Nonprofit"}
      name={"owner"}
      value={nonprofitId}
      checked={creatorNonprofitId === nonprofitId}
      onChange={() => onChange(nonprofitId)}
      label={name}
    />
  );
}
